/**
 * Scheduling actions service — the client surface for every schedule mutation.
 *
 * Cancel / reschedule / request / respond / admin-override all run inside
 * callable Cloud Functions (Firestore transactions) so quota checks, makeup
 * appending and penalties are atomic and server-authoritative — none of these
 * write Firestore directly from the browser.
 *
 * Errors: callables throw `HttpsError(code, MACHINE_CODE, details)`. The
 * wrapper translates known machine codes into friendly messages (and keeps
 * `error.code`/`error.details` for callers that need the numbers, e.g. quota
 * remaining).
 */
import { httpsCallable } from 'firebase/functions';

import { functions } from './config';

/** MACHINE_CODE → user-facing message. Fallbacks to the raw message. */
const ERROR_MESSAGES = {
  AUTH_REQUIRED: 'Please sign in and try again.',
  EMAIL_REQUIRED: 'Your account has no verified email — contact support.',
  NOT_A_STUDENT: 'Only students can perform this action.',
  NOT_A_MENTOR: 'Only mentors can perform this action.',
  ADMIN_ONLY: 'Only administrators can perform this action.',
  NOT_YOUR_CLASS: 'This class does not belong to your account.',
  CLASS_NOT_FOUND: 'This class no longer exists.',
  CLASS_ID_REQUIRED: 'Missing class reference — refresh and try again.',
  CLASS_NOT_SCHEDULED: 'This class is no longer scheduled.',
  CLASS_IN_PAST: 'This class has already started or finished.',
  CLASS_CHANGED: 'The class changed while you were deciding — refresh and try again.',
  CANCEL_QUOTA_EXHAUSTED: 'You have used all your free cancellations for this course.',
  CLASS_RESCHEDULE_LIMIT: 'This class has reached its reschedule limit (3 of 3 used).',
  MENTOR_RESCHEDULE_DENIED: 'The reschedule allowance for this student has been used up.',
  SLOT_NOT_FOUND: 'That slot no longer exists.',
  SLOT_UNAVAILABLE: 'That slot is no longer available — pick another.',
  SLOT_WRONG_MENTOR: 'That slot belongs to a different mentor.',
  SLOT_CONFLICT: 'That time clashes with another scheduled class.',
  TIME_IN_PAST: 'The proposed time is in the past.',
  REASON_REQUIRED: 'Please give a short reason.',
  OUTSIDE_STUDENT_AVAILABILITY: "The proposed time falls outside the student's availability.",
  REQUEST_NOT_FOUND: 'This request no longer exists.',
  REQUEST_NOT_PENDING: 'This request has already been answered.',
  REQUEST_EXPIRED: 'This request expired when the class start time passed.',
  PENALTY_NOT_FOUND: 'This penalty no longer exists.',
  QUOTA_NOT_FOUND: 'No quota record exists for this course yet.',
};

function friendly(err) {
  // Firebase packs HttpsError(code, message, details) into err.message/details.
  const machineCode = err?.message;
  const mapped = ERROR_MESSAGES[machineCode];
  const out = new Error(mapped || machineCode || 'Something went wrong — please try again.');
  out.code = err?.code;
  out.machineCode = machineCode;
  out.details = err?.details;
  return out;
}

function call(name) {
  const fn = httpsCallable(functions, name);
  return async (payload = {}) => {
    try {
      const result = await fn(payload);
      return result.data;
    } catch (err) {
      throw friendly(err);
    }
  };
}

export const schedulingService = {
  // ---- Student actions -------------------------------------------------------
  /** Cancel own class; a makeup is appended to the end of the course. */
  studentCancelClass: call('studentCancelClass'),
  /** Move own class into one of the mentor's open slots (max 3 per class). */
  studentRescheduleClass: call('studentRescheduleClass'),
  /** Open, future slots for this class's mentor (students can't query slots directly). */
  listMentorOpenSlots: call('listMentorOpenSlots'),
  /** Accept or reject a mentor's reschedule request. */
  respondToRescheduleRequest: call('respondToRescheduleRequest'),

  // ---- Mentor actions --------------------------------------------------------
  /** Cancel own class (1 free per student; beyond that a penalty applies). */
  mentorCancelClass: call('mentorCancelClass'),
  /** Propose a new time to the student (requires their approval). */
  mentorRequestReschedule: call('mentorRequestReschedule'),

  // ---- Admin actions ---------------------------------------------------------
  adminAdjustQuota: call('adminAdjustQuota'),
  adminWaivePenalty: call('adminWaivePenalty'),
  adminAdjustPenalty: call('adminAdjustPenalty'),
  adminForceCancel: call('adminForceCancel'),
};

export default schedulingService;
